import * as XLSX from 'xlsx';

// The sheets and labels loan-management writes in loan-service/internal/bidreport/xlsx.go. The
// workbook is the only form api gives a stored run's results in, so the summary is read from it.
const SHEET_BID_SUMMARY = 'Bid Summary';
const SHEET_OPTIMIZATION = 'Optimization';
const SHEET_EXCLUDED_BIDDERS = 'Excluded Bidders';
// loan-management now says Recommended and said Recommended / Awarded before; the two deploy
// separately, so either workbook can arrive.
const RECOMMENDED_LABELS = ['Recommended', 'Recommended / Awarded'];

export type BidReportAward = {
  bidNumber: number;
  bidder: string;
  poolNumbers: string[];
  amountCents: number;
};

export type BidReportSummary = {
  round: number | undefined;
  version: number | undefined;
  optimizerVersion: string | undefined;
  poolNumbers: string[];
  poolDescriptions: Map<string, string>;
  awards: BidReportAward[];
  unawardedPoolNumbers: string[];
  recommendedCents: number | undefined;
  bestIndividual: { totalCents: number; poolCount: number } | undefined;
  saleLevelCover: { excludedBidder: string | undefined; totalCents: number } | undefined;
  individualBidCount: number;
  linkedBidCount: number;
  excludedBidderCount: number;
};

export class BidReportUnreadableError extends Error {}

type Row = unknown[];

const isAmount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const textOf = (value: unknown): string => (value == null ? '' : String(value).trim());

// The workbook's money cells hold dollars with cents.
const toCents = (dollars: number): number => Math.round(dollars * 100);

function rowsOf(workbook: XLSX.WorkBook, sheetName: string): Row[] | undefined {
  const sheet = workbook.Sheets[sheetName];
  return sheet
    ? XLSX.utils.sheet_to_json<Row>(sheet, { header: 1, raw: true, defval: null, blankrows: true })
    : undefined;
}

function columnOf(header: string[], ...labels: string[]): number {
  const column = header.findIndex((text) => labels.includes(text));
  if (column < 0) {
    throw new BidReportUnreadableError(
      `The ${SHEET_OPTIMIZATION} sheet has no "${labels[0]}" column.`,
    );
  }
  return column;
}

type OptimizationBid = {
  bidNumber: number;
  bidder: string;
  // Indexed as the pools; undefined where the bid is not on that pool.
  sharesCents: (number | undefined)[];
  totalCents: number;
  recommended: boolean;
};

function readOptimization(rows: Row[]) {
  const headerRow = rows.findIndex((row) => textOf(row[0]) === 'Bid #');
  if (headerRow < 0) {
    throw new BidReportUnreadableError(`The ${SHEET_OPTIMIZATION} sheet has no bid table.`);
  }
  const header = rows[headerRow].map(textOf);
  const firstPoolColumn = columnOf(header, 'Bid % (cash) BV') + 1;
  const totalColumn = columnOf(header, 'Bid $ (cash)');
  const recommendedColumn = columnOf(header, ...RECOMMENDED_LABELS);
  const poolNumbers = header.slice(firstPoolColumn, totalColumn);

  const bids: OptimizationBid[] = [];
  // The row under the header holds each pool's book value.
  for (const row of rows.slice(headerRow + 2)) {
    if (!isAmount(row[0])) {
      break;
    }
    const total = row[totalColumn];
    bids.push({
      bidNumber: row[0],
      bidder: textOf(row[1]),
      sharesCents: poolNumbers.map((_, i) => {
        const share = row[firstPoolColumn + i];
        return isAmount(share) ? toCents(share) : undefined;
      }),
      totalCents: isAmount(total) ? toCents(total) : 0,
      recommended: textOf(row[recommendedColumn]) === 'Yes',
    });
  }

  const combinationTotal = (...labels: string[]): number | undefined => {
    const total = rows.find((row) => labels.includes(textOf(row[0])))?.[totalColumn];
    return isAmount(total) ? toCents(total) : undefined;
  };

  const runLine = rows
    .slice(0, headerRow)
    .map((row) => textOf(row[0]).match(/Round (\d+)\s+Optimization Run (\d+)/))
    .find(Boolean);
  const optimizerVersion = rows
    .slice(0, headerRow)
    .map((row) => textOf(row[0]).match(/^Optimizer version: (.+)$/))
    .find(Boolean)?.[1];

  return {
    poolNumbers,
    bids,
    recommendedCents: combinationTotal(...RECOMMENDED_LABELS.map((label) => `${label} $`)),
    saleLevelCoverCents: combinationTotal('Sale-Level Cover $'),
    round: runLine ? Number(runLine[1]) : undefined,
    version: runLine ? Number(runLine[2]) : undefined,
    optimizerVersion,
  };
}

function readPoolDescriptions(rows: Row[] | undefined): Map<string, string> {
  const descriptions = new Map<string, string>();
  const headerRow = (rows ?? []).findIndex(
    (row) => textOf(row[0]) === 'Pool No.' && textOf(row[1]) === 'Pool Description',
  );
  if (!rows || headerRow < 0) {
    return descriptions;
  }
  for (const row of rows.slice(headerRow + 1)) {
    const poolNumber = textOf(row[0]);
    if (poolNumber === '' || poolNumber === 'Total') {
      break;
    }
    descriptions.set(poolNumber, textOf(row[1]));
  }
  return descriptions;
}

function readSaleLevelCoverBidder(rows: Row[] | undefined): string | undefined {
  const label = textOf(rows?.find((row) => textOf(row[0]) === 'Sale-Level Cover')?.[1]);
  const prefix = 'With no bid from ';
  return label.startsWith(prefix) ? label.slice(prefix.length) : undefined;
}

function countExcludedBidders(rows: Row[] | undefined): number {
  const headerRow = (rows ?? []).findIndex((row) => textOf(row[0]) === 'Bidder');
  if (!rows || headerRow < 0) {
    return 0;
  }
  const end = rows.findIndex((row, i) => i > headerRow && textOf(row[0]) === '');
  return (end < 0 ? rows.length : end) - headerRow - 1;
}

// The highest individual bid on each pool, summed: what the sale recovers without linked bids.
function bestIndividualBids(poolNumbers: string[], bids: OptimizationBid[]) {
  const individual = bids.filter((bid) => bid.sharesCents.filter(isAmount).length === 1);
  const bestPerPool = poolNumbers
    .map((_, i) => Math.max(...individual.map((bid) => bid.sharesCents[i] ?? -Infinity)))
    .filter(Number.isFinite);
  return bestPerPool.length === 0
    ? undefined
    : {
        totalCents: bestPerPool.reduce((sum, cents) => sum + cents, 0),
        poolCount: bestPerPool.length,
      };
}

export function readBidReport(contentBase64: string): BidReportSummary {
  let workbook: XLSX.WorkBook;
  try {
    workbook = XLSX.read(contentBase64, { type: 'base64' });
  } catch {
    throw new BidReportUnreadableError('The bid report is not a workbook.');
  }
  const optimizationRows = rowsOf(workbook, SHEET_OPTIMIZATION);
  if (!optimizationRows) {
    throw new BidReportUnreadableError(`The bid report has no ${SHEET_OPTIMIZATION} sheet.`);
  }
  const optimization = readOptimization(optimizationRows);
  const summaryRows = rowsOf(workbook, SHEET_BID_SUMMARY);

  const awards = optimization.bids
    .filter((bid) => bid.recommended)
    .map((bid) => ({
      bidNumber: bid.bidNumber,
      bidder: bid.bidder,
      poolNumbers: optimization.poolNumbers.filter((_, i) => bid.sharesCents[i] !== undefined),
      amountCents: bid.totalCents,
    }));
  const awarded = new Set(awards.flatMap((award) => award.poolNumbers));
  const linkedBidCount = optimization.bids.filter(
    (bid) => bid.sharesCents.filter(isAmount).length > 1,
  ).length;

  return {
    round: optimization.round,
    version: optimization.version,
    optimizerVersion: optimization.optimizerVersion,
    poolNumbers: optimization.poolNumbers,
    poolDescriptions: readPoolDescriptions(summaryRows),
    awards,
    unawardedPoolNumbers: optimization.poolNumbers.filter((pool) => !awarded.has(pool)),
    recommendedCents: optimization.recommendedCents,
    bestIndividual: bestIndividualBids(optimization.poolNumbers, optimization.bids),
    saleLevelCover:
      optimization.saleLevelCoverCents === undefined
        ? undefined
        : {
            excludedBidder: readSaleLevelCoverBidder(summaryRows),
            totalCents: optimization.saleLevelCoverCents,
          },
    individualBidCount: optimization.bids.length - linkedBidCount,
    linkedBidCount,
    excludedBidderCount: countExcludedBidders(rowsOf(workbook, SHEET_EXCLUDED_BIDDERS)),
  };
}

// Safari can cancel a download whose object URL is revoked in the same task as the click.
// FileSaver.js waits 40 s for the same reason.
export const DOWNLOAD_URL_REVOKE_DELAY_MS = 40_000;

export function downloadBase64File(fileName: string, contentType: string, contentBase64: string) {
  const bytes = Uint8Array.from(atob(contentBase64), (char) => char.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: contentType }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), DOWNLOAD_URL_REVOKE_DELAY_MS);
}
